import React, { useState, useEffect } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  ClipboardIcon
} from '@heroicons/react/24/outline';
import { Button } from '../common/Button';
import { googleCalendarService } from '../../services/googleCalendarService';

interface GoogleAuthButtonProps {
  onAuthChange?: (isAuthenticated: boolean) => void;
  onCalendarSelect?: (calendarId: string, calendarName: string) => void;
  selectedCalendarId?: string;
  className?: string;
}

const GoogleAuthButton: React.FC<GoogleAuthButtonProps> = ({ 
  onAuthChange,
  onCalendarSelect,
  selectedCalendarId,
  className = '' 
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ 
    success: boolean; 
    message: string; 
    calendarsFound?: number;
    calendars?: Array<{ id: string; summary: string; primary?: boolean }>;
    userEmail?: string;
  } | null>(null);

  useEffect(() => {
    // Ellenőrizzük, hogy a felhasználó Google-lal jelentkezett-e be
    const checkAuth = async () => {
      setIsLoading(true);
      try {
        // Ellenőrizzük a Firebase Auth-ot
        const { auth } = await import('../../lib/firebase');
        const currentUser = auth.currentUser;
        
        if (currentUser) {
          // Ellenőrizzük, hogy Google provider van-e
          const isGoogleUser = currentUser.providerData.some(p => p.providerId === 'google.com');
          
          if (isGoogleUser) {
            setIsAuthenticated(true);
            onAuthChange?.(true);
            setTestResult({ 
              success: true, 
              message: 'Google-fiókkal bejelentkezve',
              userEmail: currentUser.email || undefined
            });
          } else {
            setIsAuthenticated(false);
            onAuthChange?.(false);
            setError('Kérlek, jelentkezz be Google-fiókkal a Calendar integráció használatához');
          }
        } else {
          setIsAuthenticated(false);
          onAuthChange?.(false);
        }
      } catch (error) {
        console.error('Auth check error:', error);
        setError('Hiba történt az integráció ellenőrzése során');
      } finally {
        setIsLoading(false);
      }
    };

    // Debounce: csak egyszer fusson le
    const timeoutId = setTimeout(checkAuth, 100);
    return () => clearTimeout(timeoutId);
  }, []); // Eltávolítjuk az onAuthChange dependency-t

  const handleTestIntegration = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { auth } = await import('../../lib/firebase');
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        setError('Nem vagy bejelentkezve');
        setIsAuthenticated(false);
        onAuthChange?.(false);
        return;
      }
      
      const isGoogleUser = currentUser.providerData.some(p => p.providerId === 'google.com');
      
      if (!isGoogleUser) {
        setError('Kérlek, jelentkezz be Google-fiókkal');
        setIsAuthenticated(false);
        onAuthChange?.(false);
        return;
      }
      
      // Sikeres
      setTestResult({
        success: true,
        message: 'Google Calendar integráció aktív',
        userEmail: currentUser.email || undefined
      });
      setIsAuthenticated(true);
      onAuthChange?.(true);
      
    } catch (error) {
      console.error('Integration test error:', error);
      setError('Hiba történt az integráció tesztelése során');
      setIsAuthenticated(false);
      onAuthChange?.(false);
    } finally {
      setIsLoading(false);
    }
  };

  if (isAuthenticated && testResult?.success) {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className="flex items-center space-x-2 text-green-600">
          <CheckCircleIcon className="h-5 w-5" />
          <span className="text-sm font-medium">Google Calendar integráció aktív</span>
        </div>
        
        {testResult && testResult.userEmail && (
          <div className="flex items-center space-x-2 text-blue-600 dark:text-blue-400">
            <InformationCircleIcon className="h-4 w-4" />
            <span className="text-xs">
              Bejelentkezve: {testResult.userEmail}
            </span>
          </div>
        )}
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleTestIntegration}
            disabled={isLoading}
            className="text-xs"
          >
            {isLoading ? (
              <>
                <ArrowPathIcon className="h-3 w-3 animate-spin mr-1" />
                Tesztelés...
              </>
            ) : (
              'Integráció tesztelése'
            )}
          </Button>
        </div>
      </div>
    );
  }


  return (
    <div className={`space-y-3 ${className}`}>
      {error && (
        <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
          <ExclamationTriangleIcon className="h-4 w-4" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <InformationCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium mb-2">✅ Google Calendar integráció</p>
            <p className="mb-3">
              Az időpontfoglalásokhoz automatikusan generálunk <strong>Google Meet linkeket</strong>.
            </p>
            <div className="bg-blue-100 dark:bg-blue-800/30 rounded-md p-3 mb-3">
              <p className="font-medium mb-1">Hogyan működik?</p>
              <ol className="list-decimal list-inside space-y-1 text-xs">
                <li>Jelentkezz be Google-fiókkal (már megtörtént ✓)</li>
                <li>Amikor valaki foglal, automatikusan létrejön egy Calendar esemény</li>
                <li>A rendszer generál egy Google Meet linket</li>
                <li>Mindkét fél kap emailt a Meet linkkel</li>
              </ol>
            </div>
            <p className="text-xs text-blue-700 dark:text-blue-300">
              💡 <strong>Tipp:</strong> Az események az elsődleges (primary) naptáradban jelennek meg.
            </p>
          </div>
        </div>
      </div>
      
      <Button
        variant="outline"
        onClick={handleTestIntegration}
        disabled={isLoading}
        className="w-full flex items-center justify-center space-x-2"
      >
        {isLoading ? (
          <>
            <ArrowPathIcon className="h-4 w-4 animate-spin" />
            <span>Ellenőrzés...</span>
          </>
        ) : (
          <>
            <CheckCircleIcon className="h-4 w-4" />
            <span>Integráció ellenőrzése</span>
          </>
        )}
      </Button>
    </div>
  );
};

export default GoogleAuthButton;
